import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useGetApiV1ExamRevisionLangBotexamlangidTranslation } from '@/shared/api/generated';
import type { BotExamLangTranslationQuestionViewModel } from '@/shared/api/generated/types/BotExamLangTranslationQuestionViewModel';
import type { QuestionTypeFilter, RevisionTranslationQuestion } from '../types/revision';
import { QUESTION_TYPE_FILTERS } from '../utils/questionType';
import {
  toRevisionTranslationQuestion,
  toTranslationSavePayload,
} from '../utils/revisionTranslationMapper';

/**
 * Tamanho de página da tradução. As questões são carregadas de uma vez (bloco
 * único) para permitir filtro por tipo client-side, navegação livre e derivar os
 * filtros disponíveis a partir dos tipos realmente presentes na prova.
 */
const TRANSLATION_PAGE_SIZE = 100;

/**
 * Carrega a tradução das questões de um idioma a partir de
 * `GET /api/v1/exam/revision/lang/{botExamLangId}/translation` (bloco único),
 * mantendo navegação, filtro por tipo e edição local (buffer). A persistência por
 * questão é feita pelo consumidor via {@link useSaveRevisionTranslationQuestion}.
 *
 * @param botExamLangId - ID do idioma da prova a traduzir/revisar
 * @returns Questão atual, navegação, filtro de tipo, filtros disponíveis e estados
 *
 * @example
 * ```tsx
 * const { current, currentIndex, total, goTo } = useRevisionTranslation(langId);
 * ```
 */
export function useRevisionTranslation(botExamLangId: number) {
  const [typeFilter, setTypeFilterState] = useState<QuestionTypeFilter>('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [overrides, setOverrides] = useState<Record<number, Partial<RevisionTranslationQuestion>>>(
    {},
  );

  const query = useGetApiV1ExamRevisionLangBotexamlangidTranslation(
    botExamLangId,
    { pageNumber: 1, pageSize: TRANSLATION_PAGE_SIZE },
    { query: { enabled: botExamLangId > 0, placeholderData: keepPreviousData } },
  );

  const data = query.data ?? null;
  const rawQuestions = data?.questions ?? [];

  const questions = rawQuestions
    .map((dto, i) => toRevisionTranslationQuestion(dto, i + 1))
    .map(question => ({ ...question, ...overrides[question.id] }));

  // Filtros disponíveis: "Todos" + apenas os tipos efetivamente presentes na prova.
  const presentTypes = new Set(questions.map(question => question.questionType));
  const availableFilters = QUESTION_TYPE_FILTERS.filter(
    filter => filter === 'all' || presentTypes.has(filter),
  );

  const filtered =
    typeFilter === 'all'
      ? questions
      : questions.filter(question => question.questionType === typeFilter);

  const total = filtered.length;
  const safeIndex = Math.min(currentIndex, Math.max(0, total - 1));
  const current = filtered[safeIndex] ?? null;

  const goTo = useCallback((index: number) => {
    setCurrentIndex(Math.max(0, index));
  }, []);

  // Ao trocar o filtro de tipo, volta para a primeira questão do conjunto filtrado.
  const setTypeFilter = useCallback((type: QuestionTypeFilter) => {
    setTypeFilterState(type);
    setCurrentIndex(0);
  }, []);

  const updateTranslation = useCallback(
    (id: number, patch: Partial<RevisionTranslationQuestion>) => {
      setOverrides(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    },
    [],
  );

  const buildSavePayload = useCallback((): BotExamLangTranslationQuestionViewModel | null => {
    if (!current) return null;
    const dto = rawQuestions.find(item => (item.questionId ?? -1) === current.id);
    if (!dto) return null;
    return toTranslationSavePayload(dto, current, botExamLangId);
  }, [current, rawQuestions, botExamLangId]);

  return {
    current,
    currentIndex: safeIndex,
    total,
    /** Total de questões da prova (sem aplicar o filtro de tipo). */
    examTotal: questions.length,
    typeFilter,
    /** "Todos" + apenas os tipos de questão presentes na prova carregada. */
    availableFilters,
    setTypeFilter,
    goTo,
    updateTranslation,
    /** Constrói o payload de persistência da tradução da questão atual (ou `null`). */
    buildSavePayload,
    /** Título da prova no idioma alvo (para o cabeçalho quando o grid não está disponível). */
    title: data?.title ?? null,
    /** Código de cultura do idioma alvo (para o cabeçalho quando o grid não está disponível). */
    targetLanguageInfo: data?.targetLanguageInfo ?? null,
    isLoading: query.isLoading,
    /** `true` enquanto uma nova página é buscada (navegação), com dados anteriores em tela. */
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  } as const;
}
